// Research category content.
//
// Research pieces are long-form, source-heavy, and structured, so they live h
// as blocks rather than in the Supabase `articles` table, which stores plain
// newline-separated paragraphs and cannot represent tables, citations or
// reference lists.

export const RESEARCH_CATEGORY = {
  title: 'Research',
  tagline:
    'Long-form, source-checked analysis. Primary studies only, with methodology and limitations stated up front.',
};

const digitalMarketing2026 = {
  slug: 'digital-marketing-2026-what-the-data-says',
  title: 'Digital Marketing in 2026: What the Data Says, and Who Is Actually Measuring It',
  subheader: 'Five findings from independent practitioners, and what a small team should do about them',
  publishedAt: '2026-09-19',
  readingTime: '18 min read',
  tags: ['SEO', 'AI Search', 'Paid Media', 'Measurement'],
  summary:
    'Zero-click search crossed 68% of US Google queries, Google began rewarding business model over content quality, AI crawlers are being blocked at the infrastructure layer, and Meta CPMs rose 20% year over year. A synthesis of four primary sources, with their limitations stated.',
  blocks: [
    {
      type: 'h2',
      text: 'Why these sources',
    },
    {
      type: 'p',
      text:
        'The request was for credible sources from accomplished practitioners or very small, high-output businesses rather than large agency content teams. The four primary sources below all qualify:',
    },
    {
      type: 'sources',
      items: [
        {
          name: 'SparkToro',
          person: 'Rand Fishkin',
          detail:
            'A software company with roughly half a dozen employees that publishes some of the most-cited original search research in the industry. Fishkin previously founded Moz. SparkToro’s zero-click research series dates back to 2016 and is run in partnership with clickstream data providers.',
        },
        {
          name: 'Zyppy SEO',
          person: 'Cyrus Shepard',
          detail:
            'A boutique consultancy of a handful of people. Shepard was formerly Head of SEO and Audience Growth at Moz and has advised Amazon. He publishes original correlation studies through his Zyppy Signal newsletter.',
        },
        {
          name: 'Growth Memo',
          person: 'Kevin Indig',
          detail:
            'Effectively a one-person research and advisory business. Indig previously ran growth at Shopify and G2 and now publishes original AI-search studies to a paid subscriber base of over 23,000.',
        },
        {
          name: 'Orainti',
          person: 'Aleyda Solís',
          detail:
            'A deliberately small, boutique SEO and AI search consultancy advising enterprise brands worldwide. Orainti was founded in 2014 by Solís after working at agencies and in-house, and she has over 19 years of SEO experience across European, American and Latin-American companies. She explicitly rejected agency scale: “I really like to talk with customers. That is why I do not have the big agency.”',
          cite: ['Orainti', 'Humans of Martech'],
        },
      ],
    },
    {
      type: 'p',
      text:
        'A fifth reference point, Marketing Examples (Harry Dry), is used for the messaging section. Dry built Marketing Examples to more than 130,000 email subscribers as a one-person content business publishing short, specific copy and design case studies.',
      cite: ['Libraryofllm'],
    },

    {
      type: 'h2',
      text: 'Finding 1: The traffic economy underneath SEO has structurally broken',
    },
    {
      type: 'p',
      text: 'This is the single most important quantified shift of the last two years.',
    },
    {
      type: 'p',
      text:
        'SparkToro’s June 2026 study, using Similarweb’s desktop and mobile clickstream panel for January through April 2026, found that 68.01% of US Google searches ended without a click. That is up from 60.45% in 2024, a 7.5 percentage point rise and the fastest acceleration of the phenomenon in a decade.',
      cite: ['SparkToro'],
    },
    {
      type: 'p',
      text:
        'The per-thousand framing is more useful than the percentage. For every 1,000 US Google searches, only about 276 clicks now reach the open web. In 2024 that figure was 374, meaning the open web lost roughly a quarter of its clicks per thousand searches in two years.',
      cite: ['Pravinkumar'],
    },
    {
      type: 'p',
      text: 'Two drivers, and one non-driver:',
    },
    {
      type: 'p',
      text:
        'AI Overviews now appear on more than 20% of all searches and reduce click-through rate by nearly 60% when present. Meanwhile, the share of searches that lead to another Google search rose 7.2 points from 2024 to 2026, showing Google succeeding at keeping users inside its own ecosystem.',
      cite: ['SparkToro'],
    },
    {
      type: 'p',
      text:
        'The non-driver is AI Mode. Only 0.34% of searches reached AI Mode between January and April 2026, although Google stated at I/O 2026 that AI Mode had passed 1 billion monthly users with queries more than doubling each quarter. So AI Mode is not what broke the click economy in 2026, but it is the most likely candidate to compound it through 2027.',
      cite: ['SparkToro'],
    },
    {
      type: 'p',
      text:
        'Independent corroboration exists. Ahrefs’ tracker covering over 75,000 opted-in domains showed the share of traffic Google sends those sites fell 8 percentage points from June 2025 to May 2026, roughly a 22% drop, and these are sites with professional marketers actively working to grow traffic.',
      cite: ['SparkToro'],
    },
    {
      type: 'quote',
      text:
        'Getting better at conventional SEO will not reverse this. SEO matters as much or more than before, but it will not earn traffic the way it once did.',
      attribution: 'Rand Fishkin’s conclusion, paraphrased',
    },
    {
      type: 'p',
      text:
        'Fishkin’s position is that traffic should be replaced as a KPI in favour of a correlation dashboard and audience research into where the target audience actually pays attention.',
      cite: ['SparkToro'],
    },

    {
      type: 'h2',
      text: 'Finding 2: Google is now rewarding business model, not content quality',
    },
    {
      type: 'p',
      text:
        'Cyrus Shepard’s April 2026 study is the best available answer to the obvious follow-up question: if most sites are losing, what do the winners have in common?',
    },
    {
      type: 'p',
      text:
        'Shepard analysed over 400 winning and losing websites, starting from the dataset in Lily Ray’s analysis of Google’s December 2025 core update, pulled 12-month year-over-year traffic change from the Ahrefs API, hand-classified each site against a fixed set of binary feature flags, and calculated Spearman rank correlations against traffic change.',
      cite: ['Zyppy'],
    },
    {
      type: 'p',
      text:
        'Five features predicted outcomes, with correlation coefficients and the split between winners and losers:',
    },
    {
      type: 'table',
      headers: ['Feature', 'Correlation', 'Winners', 'Losers'],
      rows: [
        ['Offers a product or service', '0.391', '70.2%', '34.6%'],
        ['Allows task completion', '0.381', '83.7%', '50.2%'],
        ['Proprietary assets', '0.357', '92.9%', '57.1%'],
        ['Tight topical focus', '0.250', '75.9%', '61.3%'],
        ['Strong brand', '0.206', '32.6%', '16.1%'],
      ],
    },
    {
      type: 'p',
      text:
        'Losers tended to be news, informational and affiliate sites. Sites selling their own products performed especially well compared to third-party platforms, and service-based offerings such as subscriptions and digital goods also did well.',
      cite: ['Zyppy'],
    },
    {
      type: 'p',
      text:
        'The most actionable part of the study is the additive effect. Win rates by number of features present were 13.5% at zero features, 15.4% at one, 22.0% at two, 30.7% at three, then a sharp jump to 68.1% at four and 69.7% at five. The cliff between three and four features is the practical finding: partial compliance buys you almost nothing.',
      cite: ['Zyppy'],
    },
    {
      type: 'p',
      text:
        'Two definitions are worth getting right. Task completion means the user can finish what they came to do on your site, and it does not require selling anything: calculators, quizzes, searchable databases and booking tools all count. Proprietary assets means owning something others cannot easily replicate, such as unique products, special databases, user-generated content, software or reviews.',
      cite: ['Zyppy'],
    },
    {
      type: 'p',
      text:
        'Shepard also reported what did not correlate, which is unusual and raises the study’s credibility. First-hand experience, user-generated content or community platforms, and uniqueness of information showed no bearing in the data, though he suspects this is because those factors were baked into the algorithm long ago rather than because Google ignores them.',
      cite: ['Zyppy'],
    },
    {
      type: 'note',
      title: 'Stated caveat',
      text:
        'The findings are correlational and rely on manual classification, so they indicate association and cannot establish causation.',
      cite: ['Zyppy'],
    },

    {
      type: 'h2',
      text: 'Finding 3: AI search optimization is mostly technical hygiene plus brand presence',
    },
    {
      type: 'p',
      text:
        'Aleyda Solís’ operational findings from client work in early 2026 are the most specific practitioner account available, and they are unglamorous.',
    },
    {
      type: 'h3',
      text: 'Crawler access is failing silently at the infrastructure layer',
    },
    {
      type: 'p',
      text:
        'Hosting rules, CDN settings and robots files frequently permit Googlebot while quietly blocking newer AI user agents, and Solís has run audit after audit where AI crawlers never reached critical sections of a site.',
      cite: ['Humans of Martech'],
    },
    {
      type: 'quote',
      text:
        'I realized my hosting company was blocking AI bots. All the answers looked wrong and the share of voice was terrible.',
      attribution: 'Aleyda Solís, on her own property',
    },
    {
      type: 'p',
      text:
        'The lesson for anyone running their own infrastructure is that AI visibility diagnosis must start at the edge, not in a content audit.',
      cite: ['Humans of Martech'],
    },
    {
      type: 'h3',
      text: 'AI crawlers do not execute JavaScript',
    },
    {
      type: 'p',
      text:
        'They ingest raw markup and move on, so sites leaning on client-side rendering lose entire menus, product details, pricing tables and conversion paths. Googlebot renders JavaScript and works through most challenges; AI crawlers are the outlier that does not. For a stack running React or similar front-ends, this reframes server-side rendering from a performance nicety into a discovery requirement.',
      cite: ['Humans of Martech'],
    },
    {
      type: 'h3',
      text: 'Prompts are noise; topics are signal',
    },
    {
      type: 'p',
      text:
        'Solís treats prompt trackers as colour rather than strategy, because prompts scatter across millions of variants and answers shift with context, history and location. Her working structure is to define the topic cluster, identify the competitors appearing inside it, measure how often each brand is mentioned and how people feel about those mentions, then track a handful of representative prompts over time.',
      cite: ['Humans of Martech'],
    },
    {
      type: 'h3',
      text: 'Community signals are doing heavy lifting',
    },
    {
      type: 'p',
      text:
        'She reports startups attributing roughly 90% of recent customers to ChatGPT citations, built not on taxonomies or backlink profiles but on YouTube product walkthroughs, Discord answers, open social posting and small communities. Her read is that LLMs track repeated mentions as ambient trust signals, rely on conversational proof, reward highly specific problem-solving content even from tiny sites, and respond quickly to new topics so early movers gain visibility first.',
      cite: ['Humans of Martech'],
    },
    {
      type: 'p',
      text: 'Kevin Indig’s quantitative work points the same direction from a different angle:',
    },
    {
      type: 'list',
      items: [
        {
          text:
            'His 2026 research found that web search position has the greatest impact on LLM citation rates, which confirms that GEO remains tied to traditional SEO because LLMs rely on web search, grounding and retrieval to produce answers. A page at position 1 has roughly a 58% chance of being cited, dropping to about 14% by position 10.',
          cite: ['The Digital Bloom', 'Position Digital'],
        },
        {
          text:
            '44.2% of LLM citations come from the first 30% of an article, which makes the introduction the highest-leverage part of the page. Answer first, explain second, add nuance third.',
          cite: ['SEO Sherpa'],
        },
        {
          text:
            'He has also documented that LLM referral traffic is fading despite strong early growth, as model changes and AI Overviews make clicks obsolete. This is the important counterweight to the GEO hype cycle: visibility inside answers, not referral volume, is the realistic outcome.',
          cite: ['Search Engine Journal'],
        },
      ],
    },

    {
      type: 'h2',
      text: 'Finding 4: Paid media is inflating, which removes the obvious fallback',
    },
    {
      type: 'p',
      text:
        'If organic clicks are drying up, the instinctive move is to buy traffic instead. The 2026 auction data says that is getting more expensive at roughly the rate organic is degrading.',
    },
    {
      type: 'p',
      text:
        'Across industries, average Meta CPM rose about 20% year over year from $11.82 to $14.19, average CPC sits around $0.78, and average CPA is $38.19, a 38.1% increase on 2025’s $27.66 baseline. Triple Whale’s data across roughly 35,000 ecommerce brands for August 2025 to July 2026 puts median CPA at $38.99 and median CPM at $15.06, with brands allocating 66.88% of total ad budget to Meta.',
      cite: ['Ryze AI', 'Triple Whale'],
    },
    {
      type: 'p',
      text:
        'Interpretation matters here more than the raw figures. One benchmark set reports CPMs rising 20% year over year while CPA rose only 1%, suggesting brands with strong creative strategy and conversion rate optimisation absorbed the cost increase, and framing the rising CPM floor as a structural competition trend rather than a performance failure. These benchmark datasets disagree on CPA direction, which is itself a finding: cross-industry averages are close to useless for decision-making, and vertical-level plus account-level baselines are the only usable comparison.',
      cite: ['Webtonic'],
    },
    {
      type: 'note',
      title: 'Evidence tier',
      text:
        'Treat these benchmark aggregators as lower-tier evidence than the four primary sources. They are vendor-published, they are US and ecommerce weighted, and their methodologies are thinner.',
    },

    {
      type: 'h2',
      text: 'Finding 5: The message layer is where small teams still have an edge',
    },
    {
      type: 'p',
      text:
        'When distribution is expensive and clicks are scarce, the compression ratio of the message matters more. Harry Dry’s framework is the most widely adopted practitioner heuristic here, and it is three tests rather than a process.',
    },
    {
      type: 'p',
      text:
        'Make the copy visualizable using specific images rather than abstract terms, make it falsifiable because specific claims beat vague praise, and make it yours alone so that a competitor could not plausibly sign the same ad. His worked example is New Balance’s line about being worn by supermodels in London and dads in Ohio: you can picture it, the claim is checkable, and the combination belongs to that brand only.',
      cite: ['SPROUTWORTH'],
    },
    {
      type: 'p',
      text:
        'The relevance to the 2026 data is direct. Zero-click means your brand is frequently encountered as a mention inside someone else’s summary, a social post or a forum reply, with no landing page to do the persuading. The sentence has to carry the whole load.',
    },

    {
      type: 'h2',
      text: 'Synthesis: an operating model for a small team',
    },
    {
      type: 'p',
      text: 'Reading the four sources together, a consistent sequence emerges.',
    },
    {
      type: 'steps',
      items: [
        {
          title: 'Fix machine access before anything else',
          text:
            'Verify that AI user agents are not blocked at the CDN, WAF or host level, not just in robots.txt. Confirm that critical content renders in raw HTML. This is cheap, it is invisible when broken, and every downstream measurement is wrong until it is done.',
        },
        {
          title: 'Audit your site against Shepard’s five features honestly',
          text:
            'The win-rate cliff sits between three and four features. If you have tight topical focus and a strong brand but no task completion and no proprietary asset, adding a genuine tool or dataset is likely the highest-return single project available. Shepard’s caution applies: sites winning offer products and services as a core part of the business, not a hasty add-on, and simply setting up a store for easy digital downloads probably will not work.',
          cite: ['Zyppy'],
        },
        {
          title: 'Build one proprietary asset per year',
          text:
            'Original research, pricing surveys, aggregated reviews, an owned dataset, an interactive tool. This is the only category that simultaneously satisfies Shepard’s strongest correlate, gives LLMs something uncopyable to cite, and produces the kind of specific, falsifiable claim Dry’s framework wants.',
        },
        {
          title: 'Restructure pages answer-first',
          text:
            'Given that 44.2% of LLM citations come from the first 30% of content, the most citable sentence belongs in the opening, not in paragraph fourteen.',
          cite: ['SEO Sherpa'],
        },
        {
          title: 'Organise by topic cluster, not by prompt list or keyword volume',
          text:
            'Solís’ warning about keyword-volume-driven strategy is that it drifts away from product and customer reality. Her stated standard is to build content you would want to have even with SEO taken out of the equation, because it helps customers and sells more.',
          cite: ['Humans of Martech'],
        },
        {
          title: 'Change the scoreboard',
          text:
            'Track presence and share of voice inside AI answers, sentiment, citation sources, branded search volume, and direct or dark-social arrival, alongside a correlation dashboard connecting marketing activity to revenue rather than to sessions.',
        },
      ],
    },

    {
      type: 'h2',
      text: 'Limitations you should carry into any use of this',
    },
    {
      type: 'p',
      text:
        'The headline data is US-only and panel-based. SparkToro’s figures come from Similarweb’s US desktop and mobile web panel, assume a two-thirds mobile to one-third desktop split, estimate paid click share from a third-party benchmark, and exclude the Google mobile app entirely, where zero-click behaviour is likely even higher. The decade-long comparison chart also mixes three different panel providers across 2016, 2024 and 2026, which Fishkin flags as comparing apples and oranges. Outside the US, read the direction, not the decimals.',
      cite: ['SparkToro'],
    },
    {
      type: 'p',
      text:
        'Shepard’s study is correlational and hand-classified, as he states himself, and the dataset was seeded from one core update’s winners and losers, which introduces selection effects.',
    },
    {
      type: 'p',
      text:
        'The much-quoted LLM conversion rate figures are contested. The frequently cited numbers of 15.9% for ChatGPT, 10.5% for Perplexity and 5% for Claude against 1.76% for Google organic come from Seer Interactive in June 2025, and practitioners have openly disputed how they are sliced. One documented exchange between Kevin Indig and Ahrefs’ Tim Soulo covered exactly this: Ahrefs’ data skews toward very high-traffic sites, while the counter-analysis used niche B2B SaaS sites with low search volumes, and the takeaway offered was that a study exists to support whichever narrative you are promoting. Treat single-source conversion multipliers as directional at best.',
      cite: ['Position Digital', 'Substack'],
    },
    {
      type: 'p',
      text:
        'Benchmark averages hide everything that matters. Meta CPM ranges across verticals by an order of magnitude. Your own trailing baseline is the only comparison worth acting on.',
    },
  ],
  references: [
    {
      text:
        'Fishkin, R. (2026, June 8). In 2026, Less than One Third of Google Searches Still Send a Click. SparkToro, with data from Similarweb.',
      url:
        'https://sparktoro.com/blog/in-2026-less-than-one-third-of-google-searches-still-send-a-click/',
    },
    {
      text:
        'Shepard, C. (2026, April 9). 5 Data-Backed Features of Websites Winning Google in 2026. Zyppy Signal.',
      url: 'https://signal.zyppy.com/p/winning-google',
    },
    {
      text:
        'Solís, A. (2026, January 13). Interview: AI search crawlability and why your site’s technical foundations decide your visibility. Humans of Martech, episode 202.',
      url: 'https://humansofmartech.com/2026/01/13/202-aleyda-solis-ai-search-crawlability/',
    },
    {
      text:
        'Indig, K. (2026). State of AI Search Optimization 2026 and related 2026 research. Growth Memo. Findings on citation position and first-30% citation share accessed via secondary reporting, as the primary post is partly paywalled.',
      url: 'https://www.growth-memo.com/p/state-of-ai-search-optimization-2026',
    },
    {
      text: 'Dry, H. Marketing Examples and Copywriting Examples.',
      url: 'https://marketingexamples.com',
    },
    {
      text:
        'Supporting benchmark data: Triple Whale Meta Ads benchmarks, August 2025 to July 2026; Ryze 2026 Meta Ads Benchmark report; Seer Interactive LLM conversion analysis (June 2025), noted above as contested.',
    },
  ],
};

export const RESEARCH_ARTICLES = [digitalMarketing2026];

export const getResearchArticle = (slug) =>
  RESEARCH_ARTICLES.find((article) => article.slug === slug);

export const formatResearchDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};
